import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useConfiguration, useUpdateGeneralConfiguration } from "@/hooks";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BAD_COLOR,
  DEFAULT_OPTIONS,
  GOOD_COLOR,
  HEADING_COLOR,
  PAPER_SIZES,
  PRIMARY_COLOR,
  TIMEZONES,
  WARNING_BAD_COLOR,
  WARNING_COLOR,
  WARNING_GOOD_COLOR,
} from "@/lib/constants";
import { useForm } from "react-hook-form";
import {
  GeneralConfigurationRequest,
  GeneralConfigurationRequestSchema,
} from "@/api";
import { zodResolver } from "@hookform/resolvers/zod";

// Import Shadcn Form components
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

export function GeneralConfiguration() {
  // query hook
  const { data: configuration, isLoading: configurationLoading } = useConfiguration();
  // mutation hooks
  const generalConfiguration = useUpdateGeneralConfiguration();

  // form hooks
  const generalForm = useForm<GeneralConfigurationRequest>({
    resolver: zodResolver(GeneralConfigurationRequestSchema),
    values: {
      primaryColor: configuration?.primaryColor ?? PRIMARY_COLOR,
      goodColor: configuration?.goodColor ?? GOOD_COLOR,
      warningColor: configuration?.warningColor ?? WARNING_COLOR,
      badColor: configuration?.badColor ?? BAD_COLOR,
      warningBadColor: configuration?.warningBadColor ?? WARNING_BAD_COLOR,
      headingColor: configuration?.headingColor ?? HEADING_COLOR,
      warningGoodColor: configuration?.warningGoodColor ?? WARNING_GOOD_COLOR,
      paperSize: configuration?.paperSize ?? DEFAULT_OPTIONS.GENERAL.PAPER_SIZE,
      marginInches:
        configuration?.marginIn ??
        Number(DEFAULT_OPTIONS.GENERAL.MARGIN_INCHES),
      timezone: configuration?.timezone ?? DEFAULT_OPTIONS.GENERAL.TIMEZONE,
      coverImage:
        configuration?.coverImage ?? DEFAULT_OPTIONS.GENERAL.COVER_IMAGE,
      laboratory: configuration?.laboratory ?? undefined,
      accreditationNumber: configuration?.accreditationNumber ?? undefined,
      approvalNumber: configuration?.approvalNumber ?? undefined,
      outline: configuration?.outline ?? DEFAULT_OPTIONS.GENERAL.OUTLINE,
      sectionDividers: configuration?.sectionDividers ?? DEFAULT_OPTIONS.GENERAL.SECTION_DIVIDERS,
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Configuración General</CardTitle>
        <CardDescription>
          Configure los parámetros aplicables a todos los reportes.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6">
        <Form {...generalForm}>
          <form className="grid gap-6">
            <div className="grid gap-2">
              <FormLabel className="text-xs font-medium">
                Opciones
              </FormLabel>
              <div className="grid gap-1">
                <FormField
                  control={generalForm.control}
                  name="coverImage"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2">
                      <FormControl>
                        <Checkbox
                          checked={!!field.value}
                          onCheckedChange={(e) => field.onChange(e)}
                          className="size-4"
                        />
                      </FormControl>
                      <FormLabel className="text-xs font-medium cursor-pointer">
                        Incluir imagen de portada
                      </FormLabel>
                    </FormItem>
                  )}
                />

                <FormField
                  control={generalForm.control}
                  name="outline"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2">
                      <FormControl>
                        <Checkbox
                          checked={!!field.value}
                          onCheckedChange={(e) => field.onChange(e)}
                          className="size-4"
                        />
                      </FormControl>
                      <FormLabel className="text-xs font-medium cursor-pointer">
                        Incluir tabla de contenido
                      </FormLabel>
                    </FormItem>
                  )}
                />

                <FormField
                  control={generalForm.control}
                  name="sectionDividers"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2">
                      <FormControl>
                        <Checkbox
                          checked={!!field.value}
                          onCheckedChange={(e) => field.onChange(e)}
                          className="size-4"
                        />
                      </FormControl>
                      <FormLabel className="text-xs font-medium cursor-pointer">
                        Incluir portadas de secciones
                      </FormLabel>
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <FormField
              control={generalForm.control}
              name="laboratory"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    Laboratorio
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={generalForm.control}
              name="accreditationNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    Número de acreditación
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={generalForm.control}
              name="approvalNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    Número de aprobación
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={generalForm.control}
              name="logoFile"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Logo</FormLabel>
                  <img
                    src={configuration?.logoUrl ?? undefined}
                    alt="Logo"
                    className="w-64 h-full object-cover border-2"
                  />
                  <FormControl>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file && file.size > 1 * 1024 * 1024) {
                          toast.error("Error al agregar su logo" , { description: "El archivo debe ser menor a 1MB"});
                          return;
                        }
                        if (file) {
                          field.onChange(file);
                        }
                      }}
                    />
                  </FormControl>
                  <FormDescription>
                    Seleccione una imagen para el logo que aparecerá en
                    los reportes.
                  </FormDescription>
                </FormItem>
              )}
            />
            <div className="grid gap-3">
              <h3 className="text-sm font-medium">Colores</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <FormField
                  control={generalForm.control}
                  name="primaryColor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <span
                          className="rounded-full size-6"
                          style={{ backgroundColor: field.value }}
                        ></span>
                        Principal
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="color"
                          value={field.value}
                          onChange={field.onChange}
                          className="hidden"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={generalForm.control}
                  name="headingColor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <span
                          className="rounded-full size-6"
                          style={{ backgroundColor: field.value }}
                        ></span>
                        Encabezados
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="color"
                          value={field.value}
                          onChange={field.onChange}
                          className="hidden"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={generalForm.control}
                  name="goodColor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <span
                          className="rounded-full size-6"
                          style={{ backgroundColor: field.value }}
                        ></span>
                        Aceptable
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="color"
                          value={field.value}
                          onChange={field.onChange}
                          className="hidden"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={generalForm.control}
                  name="warningColor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <span
                          className="rounded-full size-6"
                          style={{ backgroundColor: field.value }}
                        ></span>
                        Alerta
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="color"
                          value={field.value}
                          onChange={field.onChange}
                          className="hidden"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={generalForm.control}
                  name="warningGoodColor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <span
                          className="rounded-full size-6"
                          style={{ backgroundColor: field.value }}
                        ></span>
                        Alerta (aceptable)
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="color"
                          value={field.value}
                          onChange={field.onChange}
                          className="hidden"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={generalForm.control}
                  name="warningBadColor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <span
                          className="rounded-full size-6"
                          style={{ backgroundColor: field.value }}
                        ></span>
                        Alerta (inaceptable)
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="color"
                          value={field.value}
                          onChange={field.onChange}
                          className="hidden"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={generalForm.control}
                  name="badColor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <span
                          className="rounded-full size-6"
                          style={{ backgroundColor: field.value }}
                        ></span>
                        Inaceptable
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="color"
                          value={field.value}
                          onChange={field.onChange}
                          className="hidden"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <Separator className="my-2" />
            <h3 className="text-sm font-medium">
              Configuración del Documento
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <FormField
                control={generalForm.control}
                name="paperSize"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tamaño de Papel</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || DEFAULT_OPTIONS.GENERAL.PAPER_SIZE}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Seleccionar tamaño" />
                        </SelectTrigger>
                        <SelectContent>
                          {PAPER_SIZES.map((size) => (
                            <SelectItem
                              key={size.value}
                              value={size.value}
                            >
                              {size.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormDescription>
                      Seleccione el tamaño de papel para los reportes.
                    </FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={generalForm.control}
                name="marginInches"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Margen (pulgadas)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0.5"
                        max="1"
                        step="0.01"
                        {...field}
                        onChange={(e) =>
                          field.onChange(Number(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormDescription>
                      Margen en pulgadas para los reportes.
                    </FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={generalForm.control}
                name="timezone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Zona Horaria</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || DEFAULT_OPTIONS.GENERAL.TIMEZONE}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Seleccionar zona" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIMEZONES.map((zone) => (
                            <SelectItem
                              key={zone.value}
                              value={zone.value}
                            >
                              {zone.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormDescription>
                      Seleccione la zona horaria para los reportes.
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                onClick={generalForm.handleSubmit(async (data) => {
                  await generalConfiguration.mutateAsync(data);
                })}
                disabled={configurationLoading || generalConfiguration.isPending}
                type="button"
              >
                {generalConfiguration.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  "Guardar"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
} 